import { useCallback, useState } from 'react';

import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Mail, RefreshCw, Users } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/shadcn/radio-group';
import { Separator } from '@/components/ui/shadcn/separator';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import { cn } from '@/lib/utils';

interface DuplicateUser {
    uuid: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    position: string | null;
    department: string | null;
    phone: string | null;
    role: string | null;
    is_m365: boolean;
    is_suspended: boolean;
    created_at: string | null;
    is_suggested_keeper: boolean;
    references: Record<string, number>;
    references_total: number;
}

interface DuplicateGroup {
    hash: string;
    suggested_keeper_uuid: string;
    users: DuplicateUser[];
}

function MicrosoftMark() {
    return (
        <svg width="11" height="11" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path fill="#F25022" d="M1 1h10v10H1z" />
            <path fill="#7FBA00" d="M12 1h10v10H12z" />
            <path fill="#00A4EF" d="M1 12h10v10H1z" />
            <path fill="#FFB900" d="M12 12h10v10H12z" />
        </svg>
    );
}

function fullName(user: DuplicateUser): string {
    return `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
}

function formatDate(value: string | null): string {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

export default function UserDedupeEditor() {
    const { t } = useTranslations();
    const [groups, setGroups] = useState<DuplicateGroup[]>([]);
    const [scanned, setScanned] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [mergingHash, setMergingHash] = useState<string | null>(null);
    const [keeperByHash, setKeeperByHash] = useState<Record<string, string>>({});
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const scan = useCallback(async () => {
        setScanning(true);
        try {
            const response = await csrfFetch('/settings/system/user-dedupe/candidates?limit=100', {
                method: 'GET',
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) {
                throw new Error('scan_failed');
            }
            const data = (await response.json()) as { groups: DuplicateGroup[] };
            const nextGroups = data.groups ?? [];
            setGroups(nextGroups);
            setKeeperByHash(Object.fromEntries(nextGroups.map((group) => [group.hash, group.suggested_keeper_uuid])));
            setScanned(true);
            toast.success(
                t('settings.system.user_dedupe.scan_done', ':count duplicate group(s) found.').replace(':count', String(nextGroups.length)),
            );
        } catch {
            toast.error(t('settings.system.user_dedupe.scan_failed', 'Failed to scan for duplicate users.'));
        } finally {
            setScanning(false);
        }
    }, [t]);

    const merge = useCallback(
        async (group: DuplicateGroup) => {
            const keeperUuid = keeperByHash[group.hash] ?? group.suggested_keeper_uuid;
            const duplicateUuids = group.users.map((user) => user.uuid).filter((uuid) => uuid !== keeperUuid);

            if (duplicateUuids.length === 0) {
                return;
            }

            setMergingHash(group.hash);
            try {
                const response = await csrfFetch('/settings/system/user-dedupe/merge', {
                    method: 'POST',
                    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                    body: JSON.stringify({ keeper_uuid: keeperUuid, duplicate_uuids: duplicateUuids }),
                });
                const data = (await response.json()) as { success: boolean; merged?: number; message?: string };
                if (!response.ok || !data.success) {
                    throw new Error(data.message ?? 'merge_failed');
                }
                setGroups((previous) => previous.filter((existing) => existing.hash !== group.hash));
                toast.success(
                    t('settings.system.user_dedupe.merge_done', 'Merged :count duplicate(s) into the kept account.').replace(
                        ':count',
                        String(data.merged ?? duplicateUuids.length),
                    ),
                );
            } catch {
                toast.error(t('settings.system.user_dedupe.merge_failed', 'Failed to merge users.'));
            } finally {
                setMergingHash(null);
            }
        },
        [keeperByHash, t],
    );

    return (
        <Card>
            <CardHeader>
                <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        <CardTitle className="text-base">{t('settings.system.user_dedupe.title', 'Duplicate Users')}</CardTitle>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={scan} disabled={scanning}>
                        {scanning ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
                        {t('settings.system.user_dedupe.scan', 'Scan')}
                    </Button>
                </div>
                <CardDescription className="text-xs">
                    {t(
                        'settings.system.user_dedupe.description',
                        'Find users that resolve to the same email (e.g. a legacy account and its Microsoft 365 duplicate). Pick the account to keep — all linked records move to it, the rest are deleted.',
                    )}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {!scanned && !scanning && (
                    <p className="text-muted-foreground text-sm">
                        {t('settings.system.user_dedupe.empty_prompt', 'Run a scan to list duplicate user accounts.')}
                    </p>
                )}

                {scanned && groups.length === 0 && (
                    <p className="text-success-subtle-foreground text-sm">
                        {t('settings.system.user_dedupe.none_found', 'No duplicate users found.')}
                    </p>
                )}

                {groups.map((group) => {
                    const keeperUuid = keeperByHash[group.hash] ?? group.suggested_keeper_uuid;
                    const isMerging = mergingHash === group.hash;

                    return (
                        <div key={group.hash} className="rounded-lg border p-3">
                            <RadioGroup
                                value={keeperUuid}
                                onValueChange={(value) => setKeeperByHash((previous) => ({ ...previous, [group.hash]: value }))}
                                className="gap-2"
                            >
                                {group.users.map((user) => {
                                    const refKey = `${group.hash}:${user.uuid}`;
                                    const isExpanded = expanded[refKey] ?? false;
                                    const isKeeper = user.uuid === keeperUuid;
                                    const refEntries = Object.entries(user.references);

                                    return (
                                        <div
                                            key={user.uuid}
                                            className={cn(
                                                'rounded-md border p-2.5 transition-colors',
                                                isKeeper ? 'border-success bg-success-subtle/60' : 'bg-muted/40 border-transparent',
                                            )}
                                        >
                                            <div className="flex items-start gap-2.5">
                                                <RadioGroupItem value={user.uuid} id={refKey} className="mt-1" />
                                                <label htmlFor={refKey} className="min-w-0 flex-1 cursor-pointer">
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        <span className="text-sm font-medium">
                                                            {fullName(user) || t('settings.system.user_dedupe.unnamed', 'Unnamed')}
                                                        </span>
                                                        {user.is_m365 && (
                                                            <span title="Microsoft 365" className="inline-flex">
                                                                <MicrosoftMark />
                                                            </span>
                                                        )}
                                                        {isKeeper && (
                                                            <Badge className="bg-success text-success-foreground hover:bg-success px-1.5 py-0 text-[10px]">
                                                                {t('settings.system.user_dedupe.keep', 'Keep')}
                                                            </Badge>
                                                        )}
                                                        {user.is_suspended && (
                                                            <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                                                                {t('settings.system.user_dedupe.suspended', 'Suspended')}
                                                            </Badge>
                                                        )}
                                                        {user.role && (
                                                            <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                                                                {user.role}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                                                        <span className="inline-flex items-center gap-1">
                                                            <Mail className="h-3 w-3" />
                                                            {user.email ?? '—'}
                                                        </span>
                                                        {user.position && <span>{user.position}</span>}
                                                        {user.department && <span>{user.department}</span>}
                                                        <span>
                                                            {t('settings.system.user_dedupe.created', 'Created')}: {formatDate(user.created_at)}
                                                        </span>
                                                    </div>
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => setExpanded((previous) => ({ ...previous, [refKey]: !isExpanded }))}
                                                    className="text-muted-foreground hover:text-foreground inline-flex shrink-0 items-center gap-1 text-xs"
                                                >
                                                    {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                                    {t('settings.system.user_dedupe.references', ':count refs').replace(
                                                        ':count',
                                                        String(user.references_total),
                                                    )}
                                                </button>
                                            </div>

                                            {isExpanded && (
                                                <div className="mt-2 ml-7">
                                                    {refEntries.length === 0 ? (
                                                        <p className="text-muted-foreground text-xs italic">
                                                            {t('settings.system.user_dedupe.no_references', 'No linked records — safe to delete.')}
                                                        </p>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-1">
                                                            {refEntries.map(([key, count]) => (
                                                                <Badge key={key} variant="outline" className="px-1.5 py-0 text-[10px] font-normal">
                                                                    {key}: {count}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </RadioGroup>

                            <Separator className="my-2.5" />

                            <div className="flex items-center justify-between gap-2">
                                <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <AlertTriangle className="text-warning-subtle-foreground h-3.5 w-3.5" />
                                    {t(
                                        'settings.system.user_dedupe.merge_hint',
                                        ':count account(s) will be deleted after their records move to the kept account.',
                                    ).replace(':count', String(group.users.length - 1))}
                                </p>
                                <Button type="button" size="sm" onClick={() => merge(group)} disabled={isMerging}>
                                    {isMerging && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                                    {t('settings.system.user_dedupe.merge', 'Merge')}
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
}
